import { cn } from '@/lib/utils';
import { ViewMode, useCalculatorStore } from '@/store/calculator';
import { Columns, Layers, LayoutGrid } from 'lucide-react';
import { motion } from 'framer-motion';

interface ViewModeSelectorProps {
  currentMode: ViewMode;
  onChange: (mode: ViewMode) => void;
}

export function ViewModeSelector({ currentMode, onChange }: ViewModeSelectorProps) {
  const { target, clearVariables, addRowVariable, addColumnVariable } = useCalculatorStore();
  
  const viewModes = [
    {
      id: 'snapshot' as ViewMode,
      label: 'Snapshot',
      icon: Layers,
      description: 'View a single calculation result'
    },
    {
      id: 'compare' as ViewMode,
      label: 'Compare',
      icon: Columns,
      description: 'Compare multiple scenarios in a list'
    },
    {
      id: 'power' as ViewMode,
      label: 'Matrix',
      icon: LayoutGrid,
      description: 'Compare scenarios in a grid'
    }
  ];

  const handleModeChange = (mode: ViewMode) => {
    // If current mode is the same as selected, do nothing
    if (mode === currentMode) return;

    // Clear variables first
    clearVariables();
    
    // Different behavior based on the selected mode
    if (mode === 'snapshot') {
      // For snapshot, we leave variables empty
    } 
    else if (mode === 'compare') {
      // For compare mode, select variables based on target
      switch (target) {
        case 'price': // Home affordability
        case 'payment': // Mortgage payment
          addRowVariable('interest');
          break;
        case 'date': // Purchase date
          addRowVariable('savings');
          break;
        case 'savings': // Down payment
          addRowVariable('price');
          break;
      }
    }
    else if (mode === 'power') {
      // For matrix mode, select row and column variables based on target
      switch (target) {
        case 'price': // Home affordability
          addRowVariable('income');
          addColumnVariable('interest');
          break;
        case 'payment': // Mortgage payment
          addRowVariable('price');
          addColumnVariable('interest');
          break;
        case 'date': // Purchase date
          addRowVariable('price');
          addColumnVariable('savings');
          break;
        case 'savings': // Down payment
          addRowVariable('date');
          addColumnVariable('price');
          break;
      }
    }

    // Now change the mode
    onChange(mode);
  };

  return (
    <div className="flex items-center bg-gray-50 p-1.5 rounded-lg mb-2 overflow-hidden">
      {viewModes.map((mode) => {
        const isActive = currentMode === mode.id;
        
        return (
          <button
            key={mode.id}
            onClick={() => handleModeChange(mode.id)}
            className={cn(
              "relative flex items-center flex-1 justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-colors",
              isActive 
                ? 'text-white' 
                : 'text-[#566577] hover:text-[#1E2A36] hover:bg-white/60'
            )}
          >
            {isActive && (
              <motion.div
                layoutId="active-tab-bg"
                className="absolute inset-0 rounded-md"
                style={{
                  background: "linear-gradient(135deg, #0A324A 0%, #1A5173 100%)"
                }}
                initial={false}
                transition={{ 
                  type: "spring", 
                  stiffness: 500, 
                  damping: 30 
                }}
              />
            )}
            
            <span className="relative flex items-center gap-1.5">
              <mode.icon className="w-4 h-4" />
              <span>{mode.label}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}